import { Button } from '@fox/atoms/src/button/index';
import { Icon } from '@fox/atoms/src/icon/index';
import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { Select } from '@fox/atoms/src/select/index';
import { TextField } from '@fox/atoms/src/text-field/index';
import { digitsOnly } from '@fox/atoms/src/inputs/currency-input';
import { useTempDataContext } from '@fox/atoms/src/temp-data-context/index';
import moment from 'moment';
import { z } from 'zod';

import { CreditCardOff } from '@fox/icons/mui-icons';
import { FlexColumnSpaced, FlexRowSpaced, FlexRowSpacedRightAligned } from '@fox/layout/src/flex';
import { zodResolver } from '@hookform/resolvers/zod';
import { nanoid } from 'nanoid';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { PaymentOptionalCvv } from '../contracts';

export type CardType = { cardHolder: string; cardNumber: string; expires: { month: string; year: string }; cvv?: string };

export const luhnCheck = (val: string) => {
	let checksum = 0; // running checksum total
	let j = 1; // takes value of 1 or 2

	// Process each digit one by one starting from the last
	for (let i = val.length - 1; i >= 0; i--) {
		let calc = 0;
		// Extract the next digit and multiply by 1 or 2 on alternative digits.
		calc = Number(val.charAt(i)) * j;

		// If the result is in two digits add 1 to the checksum total
		if (calc > 9) {
			checksum = checksum + 1;
			calc = calc - 10;
		}

		// Add the units element to the checksum total
		checksum = checksum + calc;

		// Switch the value of j
		if (j === 1) {
			j = 2;
		} else {
			j = 1;
		}
	}

	//Check if it is divisible by 10 or not.
	return checksum % 10 === 0;
};

export const CURRENT_YEAR = new Date().getFullYear();
export const twoDigitCardYear = (year: number) => String(year % 100).padStart(2, '0');
export const YEAR_OPTIONS = Array.from(new Array(10).keys()).map(a => String(a + CURRENT_YEAR));
export const MONTH_OPTIONS = Array.from(new Array(12).keys()).map(a => String(a + 1).padStart(2, '0'));

export const creditCardSchema = (includeCVV?: boolean, optionalCvv?: true) => {
	const cvvSchema = z.string().regex(/^($|[0-9]{3,4}$)/, { message: 'CVV is not valid  (should be 3-4 digits)' });
	return z.object({
		cardHolder: z.string().min(1, { message: 'This field is required.' }),
		cardNumber: z
			.string()
			.refine(val => val.length === (val[0] === '3' ? 15 : 16) && luhnCheck(val), { message: 'Card number is not valid.' }),
		expires: z
			.object({
				month: z.string(),
				year: z.string()
			})
			.superRefine((val, ctx) => {
				// val will be the month and year - we should ensure these are correct
				const isValidExpiry = moment(new Date(Number(val.year), Number(val.month) - 1, 1))
					.add(1, 'month')
					.add(-1, 'day')
					.isAfter(moment());

				if (!isValidExpiry) {
					ctx.addIssue({ path: ['month'], code: z.ZodIssueCode.custom, message: 'Expiry must be in the future.' });
				}
			}),
		...(includeCVV ? { cvv: optionalCvv ? cvvSchema.optional() : cvvSchema } : {})
	});
};

export function CardBrand(cardNo: string) {
	if (!cardNo || !cardNo.length) return null;
	const cardDigits = digitsOnly(cardNo);
	if (cardDigits.startsWith('2') || cardNo.startsWith('5')) return 'mastercard';
	if (cardDigits.startsWith('4')) return 'visa';
	if (cardDigits === '3') return null; //May or may not be AMEX, can't return a card yet
	if (cardDigits.startsWith('34') || cardDigits.startsWith('37')) return 'amex';
	return 'unknown';
}

export function CardBrandIcon(cardNo: string) {
	switch (CardBrand(cardNo)) {
		case 'mastercard':
			return <Icon iconName="Mastercard" />;
		case 'visa':
			return <Icon iconName="VisaCard" />;
		case 'amex':
			return <Icon iconName="AmexCard" />;
		case null:
		case undefined:
			return <></>;
		case 'unknown':
			return <CreditCardOff />;
	}
}

export const CARD_FORM_NAME_ON_CARD_ELEMENT = 'card-name';
export const CARD_FORM_CARD_NUMBER_ELEMENT = 'card-number';
export const CARD_FORM_EXPIRY_MONTH_ELEMENT = 'card-exp-month';
export const CARD_FORM_EXPIRY_YEAR_ELEMENT = 'card-exp-year';
export const CARD_FORM_CCV_ELEMENT = 'card-ccv';

export function CardForm({
	name,
	submitFunc,
	onCancel,
	apiErrors,
	renderCVV,
	optionalCvv,
	showSave = true,
	elementIDSuffix
}: {
	submitFunc: SubmitHandler<CardType>;
	onCancel?: () => void;
	name?: string;
	apiErrors?: string[] | null;
	renderCVV?: boolean;
	showSave?: boolean;
	elementIDSuffix?: string;
} & PaymentOptionalCvv) {
	// RHF violates the rules of hooks, which the React compiler enforces. For now we are not optimising components that 
	// use RHF, as a workaround, but eventually all of these usages need to be updated to use tanstack-form.
	// See: https://github.com/react-hook-form/react-hook-form/issues/12598
	// This string ensures the React compiler does not optimise (memoize) this component
	"use no memo"
	
	const tempData = useTempDataContext();
	const {
		register,
		handleSubmit,
		control,
		watch,
		formState: { errors, isSubmitting, isValid }
	} = useForm<CardType>({
		mode: 'onBlur',
		resolver: zodResolver(creditCardSchema(renderCVV, optionalCvv)),
		defaultValues: {
			cardHolder: name ?? (tempData?.cardFormData?.cardHolder as string),
			cardNumber: tempData?.cardFormData?.cardNumber as string,
			cvv: tempData?.cardFormData?.cvv as string,
			expires: tempData?.cardFormData?.expires
		}
	});
	if (tempData) {
		tempData.cardFormDataValid = isValid;
	}

	React.useEffect(() => {
		if (!tempData) return;
		const subscription = watch((value, { name, type }) => {
			tempData.cardFormData = value;
			tempData.dirty = true;
		});
		return () => subscription.unsubscribe();
	}, [watch]);

	// TODO - Clean this up so it's less hacky
	React.useImperativeHandle(
		r => {
			if (tempData) tempData.submitFunc = r;
		},
		() => ({
			submit: handleSubmit(submitFunc)
		})
	);
	const cardNo = watch('cardNumber');
	const suffix = elementIDSuffix || nanoid();

	return (
		<form onSubmit={handleSubmit(submitFunc)}>
			<FlexColumnSpaced>
				<TextField
					size="small"
					{...register('cardHolder')}
					status={errors.cardHolder ? 'error' : undefined}
					helperText={errors.cardHolder?.message}
					fullWidth
					label="Card Holder"
					readOnly={isSubmitting}
					id={`${CARD_FORM_NAME_ON_CARD_ELEMENT}-${suffix}`}
				/>
				<TextField
					size="small"
					{...register('cardNumber', { setValueAs: digitsOnly })}
					status={errors.cardNumber ? 'error' : undefined}
					helperText={errors.cardNumber?.message}
					fullWidth
					label="Card Number"
					readOnly={isSubmitting}
					id={`${CARD_FORM_CARD_NUMBER_ELEMENT}-${suffix}`}
					endAdornment={CardBrandIcon(cardNo)}
				/>

				<FlexRowSpaced>
					<Controller
						name="expires.month"
						control={control}
						render={({ field: { ref, onChange, ...rest } }) => (
							<Select<string, false, true, false>
								options={MONTH_OPTIONS}
								label="Month"
								disableClearable
								sx={{ flex: 1 }}
								status={errors.expires?.month ? 'error' : undefined}
								helperText={errors.expires?.month ? errors.expires?.month.message : null}
								size="small"
								onChange={(e, val) => {
									onChange(val);
								}}
								id={`${CARD_FORM_EXPIRY_MONTH_ELEMENT}-${suffix}`}
								{...rest}
							/>
						)}
					/>

					<Controller
						name="expires.year"
						control={control}
						render={({ field: { ref, onChange, ...rest } }) => (
							<Select<typeof YEAR_OPTIONS[number], false, true, false>
								options={YEAR_OPTIONS}
								label="Year"
								disableClearable
								sx={{ flex: 1 }}
								status={errors.expires?.year ? 'error' : undefined}
								helperText={errors.expires?.year ? errors.expires?.year.message : null}
								size="small"
								onChange={(e, val) => onChange(val)}
								id={`${CARD_FORM_EXPIRY_YEAR_ELEMENT}-${suffix}`}
								{...rest}
							/>
						)}
					/>
				</FlexRowSpaced>
				{renderCVV ? (
					<TextField
						size="small"
						{...register('cvv', { setValueAs: digitsOnly })}
						status={errors.cvv ? 'error' : undefined}
						helperText={errors.cvv?.message}
						label="CVV"
						readOnly={isSubmitting}
						id={`${CARD_FORM_CCV_ELEMENT}-${suffix}`}
					/>
				) : null}
				{apiErrors?.length ? (
					<MuiAlert color="error" severity="error">
						{apiErrors.map(a => (
							<React.Fragment key={a}>
								{a}
								<br />
							</React.Fragment>
						))}
					</MuiAlert>
				) : null}

				<FlexRowSpacedRightAligned>
					{onCancel ? (
						<Button loading={isSubmitting} color="secondary" variant="secondary" onClick={() => onCancel()}>
							Cancel
						</Button>
					) : null}
					{showSave ? (
						<Button type="submit" loading={isSubmitting} color="primary" variant="primary">
							Save
						</Button>
					) : null}
				</FlexRowSpacedRightAligned>
			</FlexColumnSpaced>
		</form>
	);
}
